import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Collapse } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import {
  AppDatePicker,
  AppFileUploader,
  AppInputNumber,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import { toFormData } from '../../../minions/forms';
import ModuleComsaSideBarReports from './ModuleComsaSideBarReports';
import Translator from '../../../layout/components/helpers/Translator';
import Observations from '../../../layout/screenSpecifics/Modules/ComsaReports/Observations';
import { renderCommaFloat } from '../../../minions/int';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from '../../../routing/helpers/router';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import _ from 'lodash';
import {
  getFile,
  handleMinDate,
} from '@/layout/screenSpecifics/Modules/ComsaReports/tools/comsaReportsTools';
import errorHandler from '@/layout/components/error/ErrorHandler';

const ModuleComsaWaterUseReportScreen = () => {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [reportList, setReportList] = useState([]);
  const [showSendReportModal, setShowSendReportModal] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportFormInitialValues, setReportFormInitialValues] = useState(null);
  const [reportOptions, setReportOptions] = useState([]);
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator file="user" name={`calendar.month${i + 1}`} />,
      value: i + 1,
    })),
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReportId, setDeleteReportId] = useState();
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [reportType] = useState('waterUse');
  const dynamicAreaButton = useRef(null);
  const [filters, setFilters] = useState({
    month: null,
    year: null,
    origin: null,
    cubicMetersFrom: null,
    cubicMetersTo: null,
  });
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterYear, setFilterYear] = useState(null);
  const [filterOrigin, setFilterOrigin] = useState(null);
  const [filterCubicMetersFrom, setFilterCubicMetersFrom] = useState(null);
  const [filterCubicMetersTo, setFilterCubicMetersTo] = useState(null);
  const [actualFilters, setActualFilters] = useState({
    month: null,
    year: null,
    origin: null,
    cubicMetersFrom: null,
    cubicMetersTo: null,
  });

  // > FILTERS
  const handleFilters = () => {
    const newFilters = {
      month: filterMonth,
      year: filterYear,
      origin: filterOrigin,
      cubicMetersFrom: filterCubicMetersFrom,
      cubicMetersTo: filterCubicMetersTo,
    };
    if (!_.isEqual(newFilters, actualFilters)) {
      setActualFilters(newFilters);
      setLoading(true);
      setFilters(newFilters);
    }
  };
  const handleRemoveFilters = () => {
    const newFilters = {
      month: null,
      year: null,
      origin: null,
      cubicMetersFrom: null,
      cubicMetersTo: null,
    };
    setFilterMonth(null);
    setFilterYear(null);
    setFilterOrigin(null);
    setFilterCubicMetersFrom(null);
    setFilterCubicMetersTo(null);
    setActualFilters(newFilters);
    setLoading(true);
    setFilters(newFilters);
  };
  // < FILTERS

  const [moduleData, setModuleData] = useState(null);

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_comsaReports_getModuleData'), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then(({ data }) => {
        if (isMounted.current) setModuleData(data);
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);
  // > LOAD MODULE DATA //

  // > LOAD REPORT OPTIONS //
  const loadOptions = useCallback(async () => {
    await axios.post(
        axiosPath('api_r_user_module_comsaReports_reportOptions'),
        { type: reportType, moduleBridgeHex, subModuleBridgeHex },
      )
      .then(({ data }) => {
        if (isMounted.current) setReportOptions(data);
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [reportType, moduleBridgeHex, subModuleBridgeHex]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);
  // > LOAD REPORT OPTIONS //

  // > LOAD REPORTS //
  const loadReports = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_comsaReports_report'),
        {
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          filters,
        },
      );
      return response.data;
    } catch (err) {}
  }, [filters]);

  useEffect(() => {
    if (loading) {
      loadReports().then((res) => {
        if (isMounted.current) {
          setReportList(res);
          setIsFiltersPanelOpen(false);
          setLoading(false);
        }
      });
    }
  }, [loading]);
  // < LOAD REPORTS //

  // < ADD REPORT
  const addReport = async (values) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_comsaReports_addReport'),
        toFormData({
          ...values,
          year: values.year,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    } catch (err) {}
  };

  const handleAddReport = () => {
    setShowSendReportModal(true);
    setReportFormInitialValues({
      id: null,
      month: null,
      year: null,
      dynamicArea: [{ origin: null, cubicMeters: '', comment: '' }],
      files: null,
      deletedFiles: [],
    });
  };
  // < ADD REPORT

  // < EDIT REPORT
  const editReport = async (values) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_comsaReports_editReport'),
        toFormData({
          ...values,
          year: values.year,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    } catch (err) {}
  };

  const handleEditReport = (report) => {
    const editableReport = _.omit(report, ['origin', 'volume']);
    setReportFormInitialValues({
      ...editableReport,
      month: Number(report.month),
      year: report.year,
      dynamicArea: [
        {
          origin: Number(report.origin),
          cubicMeters: report.cubicMeters,
          comment: report.comment,
        },
      ],
      deletedFiles: [],
    });
    setShowSendReportModal(true);
  };

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt)) {
      setFieldValue(
        'deletedFiles',
        deletedFiles.filter((s) => s !== salt),
      );
    } else setFieldValue('deletedFiles', [...deletedFiles, salt]);
  };
  // < EDIT REPORT

  // < DELETE REPORT
  const removeReport = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_comsaReports_removeReport'),
        {
          id: deleteReportId,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        },
      );
      return response.data;
    } catch (err) {}
  });

  useEffect(() => {
    if (deleteReportId) setShowDeleteModal(true);
  }, [deleteReportId]);

  const handleOnDeleteModalConfirm = () => {
    removeReport().then((res) => {
      setReportList(reportList.filter((r) => r.id !== res.id));
      setDeleteReportId(null);
      setShowDeleteModal(false);
    });
  };

  const handleOnDeleteModalClose = () => {
    setDeleteReportId(null);
    setShowDeleteModal(false);
  };
  // < DELETE REPORT

  const removeDynamicArea = (dynamicArea, arrayHelpers, i) => {
    if (dynamicArea.length > 1) arrayHelpers.remove(i);
  };

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    month: Yup.number()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
    year: Yup.date()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
    dynamicArea: Yup.array().of(
      Yup.object().shape({
        origin: Yup.number()
          .nullable()
          .required(
            ReactDOMServer.renderToString(
              <Translator file="user" name="user.required" />,
            ),
          ),
        cubicMeters: Yup.string()
          .typeError(
            ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="wrong.format" />,
            ),
          )
          .matches(
            /^\d+(\.\d{1,3})?$/,
            ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="0.000.format" />,
            ),
          )
          .required(
            ReactDOMServer.renderToString(
              <Translator file="user" name="user.required" />,
            ),
          ),
      }),
    ),
  });
  // < FORM SCHEMA

  // < SAVE FORM
  const handleSaveReport = (values) => {
    setIsSendingReport(true);
    const reportFormFunction = values.id ? editReport(values) : addReport(values);
    reportFormFunction
      .then(() => {
        if (isMounted.current) {
          setShowSendReportModal(false);
          setLoading(true);
        }
      })
      .finally(() => {
        if (isMounted.current) setIsSendingReport(false);
      });
  };
  // < SAVE FORM

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData.plantName} - ${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )}`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />,
                  ),
                  route: {
                    name: 'user_module_comsaReports_home',
                    params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="water.use" />,
                  ),
                },
              ]
            : [{ name: '...' }]
        }
      />
      <section className="content">
        <ModuleComsaSideBarReports
          activeRoute="user_module_comsaReports_water_use"
          moduleData={moduleData}
        />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator file="module-comsaReports" name="water.use" />
            </Title>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <span
              className="btn btn-success btn-sm mr-2"
              onClick={() => handleAddReport()}
              title={ReactDOMServer.renderToString(
                <Translator file="module-comsaReports" name="new.water.use" />,
              )}
            >
              <AppRemixIcon icon="add" />
            </span>

            <span
              className={`btn btn-sm btn-${
                isFiltersPanelOpen ? '' : 'outline-'
              }info float-right`}
              onClick={() => setIsFiltersPanelOpen(!isFiltersPanelOpen)}
            >
              <Translator file="module-comsaReports" name="filters" />
            </span>
            {(filters.month ||
              filters.year ||
              filters.origin ||
              filters.cubicMetersFrom ||
              filters.cubicMetersTo) && (
              <span
                className="btn btn-danger btn-sm mr-1 float-right"
                onClick={() => handleRemoveFilters()}
              >
                <AppRemixIcon icon="delete-bin-6" />
              </span>
            )}
          </div>
        </div>
        <Observations reportType={reportType} />
        <div className="row mt-3">
          <div className="col-12">
            <Collapse in={isFiltersPanelOpen}>
              <div className="card mb-0">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <AppDatePicker
                        title={<Translator file="module-comsaReports" name="year" />}
                        value={filterYear}
                        onChange={(e) => setFilterYear(e)}
                        minDate={handleMinDate(moduleData)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        options={months}
                        onChange={(e) => setFilterMonth(e)}
                        title={<Translator file="module-saicaReports" name="month" />}
                        icon={<AppRemixIcon icon="calendar" />}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.month"
                          />
                        }
                        value={filterMonth}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="litres" />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="from" />,
                        )})`}
                        icon={<AppRemixIcon icon="hashtag" noVariants />}
                        value={filterCubicMetersFrom || ''}
                        onChange={(number) => setFilterCubicMetersFrom(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="litres" />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="to" />,
                        )})`}
                        icon={<AppRemixIcon icon="hashtag" noVariants />}
                        value={filterCubicMetersTo || ''}
                        onChange={(number) => setFilterCubicMetersTo(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        options={!_.isEmpty(reportOptions) && reportOptions}
                        onChange={(e) => setFilterOrigin(e)}
                        title={<Translator file="module-comsaReports" name="type" />}
                        icon={<AppRemixIcon icon="drop" />}
                        placeholder={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="all.origins"
                          />,
                        )}
                        value={filterOrigin}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="float-right">
                        <span
                          className="btn btn-info"
                          onClick={() => handleFilters()}
                        >
                          <Translator file="module-saicaReports" name="filter" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <div className="card sr-header">
              <div className="card-body">
                {loading || _.isEmpty(reportOptions) ? (
                  <div>
                    <Translator file="module-saicaReports" name="loading" />
                  </div>
                ) : (
                  <>
                    {reportList.length > 0 ? (
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th className="col-1">
                              <Translator file="module-comsaReports" name="year" />
                            </th>
                            <th className="col-1">
                              <Translator file="module-comsaReports" name="month" />
                            </th>
                            <th className="col-2">
                              <Translator file="module-comsaReports" name="origin" />
                            </th>
                            <th className="col-1">m3</th>
                            <th className="col-1">
                              <Translator
                                file="module-comsaReports"
                                name="evidence"
                              />
                            </th>
                            <th className="col-5">
                              <Translator
                                file="module-comsaReports"
                                name="m-cr-comment"
                              />
                            </th>
                            <th className="col-1" />
                          </tr>
                        </thead>
                        <tbody>
                          {reportOptions.length > 0 &&
                            reportList &&
                            reportList.map((r) => (
                              <tr key={r.id}>
                                <td>{r.year}</td>
                                <td>
                                  <Translator
                                    file="user"
                                    name={`calendar.month${r.month}`}
                                  />
                                </td>
                                <td>
                                  {
                                    reportOptions.find((o) => o.value === r.origin)
                                      .label
                                  }
                                </td>
                                <td>{renderCommaFloat(r.cubicMeters)}</td>
                                <td>
                                  {r.files &&
                                    Object.keys(r.files).map((salt, index) => (
                                      <span
                                        key={index}
                                        className="mr-1 pointer"
                                        title={r.files[salt].name}
                                        onClick={() =>
                                          getFile(
                                            moduleBridgeHex,
                                            subModuleBridgeHex,
                                            plantHex,
                                            reportType,
                                            r.id,
                                            salt,
                                            dispatch,
                                          )
                                        }
                                      >
                                        <AppRemixIcon icon="file-text" />{' '}
                                      </span>
                                    ))}
                                </td>
                                <td>{r.comment}</td>
                                <td className="text-center">
                                  <span
                                    className="btn btn-info btn-xs mr-1"
                                    onClick={() => handleEditReport(r)}
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="edit"
                                      />,
                                    )}
                                  >
                                    <AppRemixIcon icon="edit" />
                                  </span>
                                  <span
                                    className="btn btn-danger btn-xs"
                                    onClick={() => setDeleteReportId(r.id)}
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="delete"
                                      />,
                                    )}
                                  >
                                    <AppRemixIcon icon="delete-bin-6" />
                                  </span>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="alert alert-info">
                        <Translator file="module-saicaReports" name="no.results" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <AppModal
          show={showSendReportModal}
          title={ReactDOMServer.renderToString(
            <Translator file="module-comsaReports" name="report.form" />,
          )}
          size="lg"
          onClose={() => setShowSendReportModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
            initialValues={reportFormInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSaveReport}
          >
            {({ setFieldValue, handleSubmit, values, errors }) => (
              <>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppDatePicker
                        title={<Translator file="module-comsaReports" name="year" />}
                        value={values.year}
                        onChange={(opt) => setFieldValue('year', opt)}
                        minDate={handleMinDate(moduleData)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                        popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      />
                      {errors.year && (
                        <div className="text-danger">*{errors.year}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppSingleSelect
                        options={months}
                        onChange={(opt) => setFieldValue('month', opt)}
                        title={<Translator file="module-saicaReports" name="month" />}
                        icon={<AppRemixIcon icon="calendar" />}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.month"
                          />
                        }
                        value={values.month}
                      />
                      {errors.month && (
                        <div className="text-danger">*{errors.month}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-12">
                    <FieldArray
                      name="dynamicArea"
                      render={(arrayHelpers) => (
                        <>
                          {values.dynamicArea.map((tv, i) => (
                            <div
                              key={i}
                              className="row pt-2 pb-2"
                              style={{ borderBottom: '1px solid #f4f6f9' }}
                            >
                              <div className="col-lg-12">
                                <button
                                  className="btn btn-danger btn-xs float-right"
                                  onClick={() =>
                                    removeDynamicArea(
                                      values.dynamicArea,
                                      arrayHelpers,
                                      i,
                                    )
                                  }
                                  disabled={values.dynamicArea.length === 1}
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="delete"
                                    />,
                                  )}
                                >
                                  <AppRemixIcon icon="delete-bin-6" />
                                </button>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <AppSingleSelect
                                    options={
                                      !_.isEmpty(reportOptions) && reportOptions
                                    }
                                    onChange={(opt) =>
                                      setFieldValue(`dynamicArea[${i}].origin`, opt)
                                    }
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="origin"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="drop" />}
                                    placeholder={
                                      <Translator
                                        file="module-comsaReports"
                                        name="select.an.origin"
                                      />
                                    }
                                    value={values.dynamicArea[i].origin}
                                  />
                                  {errors?.dynamicArea &&
                                    errors.dynamicArea[i]?.origin && (
                                      <div className="text-danger">
                                        *{errors.dynamicArea[i].origin}
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <AppInputNumber
                                    title="m3"
                                    value={values.dynamicArea[i].cubicMeters}
                                    onChange={(value) =>
                                      setFieldValue(
                                        `dynamicArea[${i}].cubicMeters`,
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    placeholder={
                                      <Translator
                                        file="module-comsaReports"
                                        name="enter.a.quantity"
                                      />
                                    }
                                    decimals={3}
                                  />
                                  {errors?.dynamicArea &&
                                    errors.dynamicArea[i]?.cubicMeters && (
                                      <div className="text-danger">
                                        *{errors.dynamicArea[i].cubicMeters}
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className="col-12">
                                <AppTextArea
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="m-cr-comment"
                                    />,
                                  )}
                                  value={values?.dynamicArea[i]?.comment}
                                  onChange={(v) =>
                                    setFieldValue(`dynamicArea[${i}].comment`, v)
                                  }
                                  height="89px"
                                />
                              </div>
                            </div>
                          ))}
                          {!values.id && (
                            <div className="row mt-3">
                              <div className="col-sm-12">
                                <span
                                  className="pointer"
                                  onClick={() =>
                                    arrayHelpers.push({
                                      origin: null,
                                      cubicMeters: '',
                                    })
                                  }
                                  ref={dynamicAreaButton}
                                >
                                  <AppRemixIcon icon="add-box" />{' '}
                                  <Translator
                                    file="module-comsaReports"
                                    name="add.origins.and.cubic.meters"
                                  />
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>
                  <div className="col-sm-12 mt-3 mb-3">
                    <label>
                      <Translator file="module-comsaReports" name="attach.evidence" />
                    </label>
                    <AppFileUploader
                      isMulti
                      placeholder={
                        !values.files || Object.keys(values?.files).length === 0
                          ? ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="select.one.multiple.files"
                              />,
                            )
                          : ReactDOMServer.renderToString(
                              <Translator
                                file="general"
                                name="select.number.of.elements"
                                variables={{
                                  elements: Object.keys(values?.files).length,
                                }}
                              />,
                            )
                      }
                      onChange={(opt) => setFieldValue('files', opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>
                </div>
                {values.id &&
                  Object.keys(reportList.find((r) => r.id === values.id).files)
                    .length > 0 && (
                    <div className="row">
                      <div className="col-12">
                        <label>
                          <Translator file="general" name="general.delete" />
                        </label>
                        <div>
                          {Object.keys(values.files).map((salt, index) => (
                            <div key={index}>
                              <label className="mb-2 pointer font-weight-light">
                                <input
                                  type="checkbox"
                                  className="mr-1"
                                  value={salt}
                                  onChange={() =>
                                    handleDeletedFiles(
                                      setFieldValue,
                                      values.deletedFiles,
                                      salt,
                                    )
                                  }
                                  checked={
                                    values.deletedFiles && values.deletedFiles[salt]
                                  }
                                />
                                {values.files[salt].name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                <div className="row">
                  <div className="col-12">
                    <button
                      className="btn btn-success btn-block"
                      type="submit"
                      onClick={handleSubmit}
                      disabled={isSendingReport}
                    >
                      <Translator
                        file="module-comsaReports"
                        name={values.id ? 'edit' : 'add'}
                      />
                    </button>
                  </div>
                </div>
              </>
            )}
          </Formik>
        </AppModal>
        <AppModal
          show={showDeleteModal}
          type="danger"
          isConfirmBtn
          isCancelBtn
          confirmBtnText={<Translator file="module-comsaReports" name="delete" />}
          title={<Translator file="module-comsaReports" name="delete.report" />}
          onClose={handleOnDeleteModalClose}
          onConfirm={handleOnDeleteModalConfirm}
        >
          <Translator file="module-comsaReports" name="confirm.delete.report" />
        </AppModal>
      </section>
    </div>
  );
};

export default ModuleComsaWaterUseReportScreen;

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;
